import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';

/**
 * 클립보드에 텍스트를 복사하는 유틸리티.
 *
 * Android 네이티브 환경에서는 Capacitor Clipboard 플러그인의 setPrimaryClip()이
 * 백그라운드 스레드에서 실행되어 조용히 실패하는 문제가 있으므로(플러그인 v8.0.x 미수정),
 * Web API를 우선 사용한다.
 *
 * 1차: navigator.clipboard (Secure Context 필요)
 * 2차: Capacitor Clipboard (iOS 네이티브에서 안정적)
 * 3차: document.execCommand('copy') (레거시 fallback)
 *
 * @returns 복사 성공 여부
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

  // 1차: navigator.clipboard (HTTPS 또는 localhost, Android WebView에서도 동작)
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 권한 거부 등 → fallback
    }
  }

  // 2차: Capacitor Clipboard (iOS 네이티브에서 안정적, Android에서는 스킵)
  if (!isAndroidNative) {
    try {
      await Clipboard.write({ string: text });
      return true;
    } catch {
      // Capacitor 웹 구현이 Clipboard API 미지원 환경에서 에러 던짐 → fallback
    }
  }

  // 3차: 레거시 execCommand fallback (HTTP 환경에서도 동작)
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch {
    console.warn('[ClipboardUtil] 클립보드 복사에 실패했습니다.');
    return false;
  }
}
